import { TempPricingPlanUiError } from '../../../../graphql/__generated/graphql';
import { convertToProcessingState } from '../../../../graphql/tourSceneConverter';
import { TourBuilderStoreEvents } from '../../events/TourBuilderStoreEvents';
import { TourBuilderStoreIoC } from '../../tourBuilderIoC';
import { TourBuilderStore } from '../../tourBuilderStore';
import { Scene, SceneGuid, SceneHelper } from '../../types/Scene';
import { TourScenePanoramaDto } from '@ael/core-api';

export type AddScenesUseCase = {
  onError: (errors: TempPricingPlanUiError[]) => void;
  panoramaIds: Set<number>;
  tourId: number;
};

export const addScenesUseCaseHandler = async (
  params: AddScenesUseCase,
  getState: () => TourBuilderStore,
  services: TourBuilderStoreIoC,
  eventHandlers: TourBuilderStoreEvents
): Promise<Partial<TourBuilderStore>> => {
  const result = await services.storageClient.createTourScene({
    tourId: params.tourId,
    panoramaSceneIds: Array.from(params.panoramaIds),
  });

  const store = getState();
  if ((result.AddTourScene.errors?.length ?? 0) > 0) {
    params.onError(result.AddTourScene.errors ?? []);
    return store;
  }

  const newScenes = new Map<SceneGuid, Scene>(store.scenes);
  const newIds = [...store.scenesById];

  result.AddTourScene.tourScene?.forEach((addedTourScene) => {
    if (!addedTourScene) {
      return;
    }
    const sceneGuid = addedTourScene.guid as SceneGuid;
    newIds.push(sceneGuid);

    const processingState = convertToProcessingState(addedTourScene.hasProcessingFinishedSuccessfully);
    const renderSettings: TourScenePanoramaDto =
      processingState === 'success' && addedTourScene.sceneRenderSettings
        ? {
            baseDir: addedTourScene.sceneRenderSettings.baseDir,
            urlSuffix: addedTourScene.sceneRenderSettings.urlSuffix,
            xmlDefinition: addedTourScene.sceneRenderSettings.xmlDefinition,
            startingSceneIndex: addedTourScene.sceneRenderSettings?.startingSceneIndex,
          }
        : SceneHelper.getDefaultRenderSettings();

    newScenes.set(sceneGuid, {
      guid: sceneGuid,
      name: addedTourScene.name,
      thumbnailUrl: addedTourScene?.thumbnailUrl,
      linkGuids: [],
      sceneProcessingState: processingState,
      renderSettings,
    });

    eventHandlers.onSceneAdded.notify({
      guid: sceneGuid,
      renderSettings,
    });
  });

  return {
    scenes: newScenes,
    scenesById: newIds,
  };
};
